import { LoadingOutlined, SaveFilled } from "@ant-design/icons";
import { Button, Checkbox, Input, message, Table } from "antd";
import { Api, ISetting } from "@model";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useIsCompact } from "../../../hooks/useIsCompact";

export type AppSettingsFormHandle = {
  saveAll: () => Promise<void>;
};

const AppSettingsForm = forwardRef<AppSettingsFormHandle>((_, ref) => {
  const isCompact = useIsCompact();
  const [settings, setSettings] = useState<ISetting[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Api.get(`/api/settings`).then((w) => {
      setSettings(w.data.data);
    });
  }, []);

  const updateSetting = (setting: ISetting) =>
    Api.put(`/api/admin/panel/settings`, {
      key: setting.key,
      title: setting.title,
      value: setting.value,
      type: setting.type,
    });

  const saveAll = useCallback(async () => {
    if (!settings) return;

    setLoading(true);
    try {
      const results = await Promise.allSettled(settings.map(updateSetting));
      const failedCount = results.filter(
        (result) => result.status === "rejected",
      ).length;

      if (failedCount) {
        message.error(
          `${failedCount} setting${failedCount === 1 ? "" : "s"} failed to update`,
        );
        return;
      }

      message.success("All settings updated successfully");
    } finally {
      setLoading(false);
    }
  }, [settings]);

  useImperativeHandle(ref, () => ({ saveAll }), [saveAll]);

  if (!settings) {
    return <LoadingOutlined />;
  }

  const controlType = (row: ISetting) => {
    if (row.type === "input" || row.type === "bool" || row.type === "multiline") {
      return row.type;
    }
    const value = String(row.value ?? "");
    if (value === "true" || value === "false") return "bool";
    if (value.includes("\n") || value.length > 80) return "multiline";
    return "input";
  };

  const renderValue = (row: ISetting) => {
    switch (controlType(row)) {
      case "input":
        return (
          <Input
            value={row.value}
            onChange={(e) => {
              settings.find((a) => a.key === row.key)!.value = e.target.value;
              setSettings([...settings]);
            }}
            size="small"
          />
        );
      case "bool":
        return (
          <Checkbox
            checked={row.value === "true"}
            onChange={(e) => {
              settings.find((a) => a.key === row.key)!.value = e.target.checked
                ? "true"
                : "false";
              setSettings([...settings]);
            }}
          />
        );
      case "multiline":
        return (
          <Input.TextArea
            rows={3}
            value={row.value}
            onChange={(e) => {
              settings.find((a) => a.key === row.key)!.value = e.target.value;
              setSettings([...settings]);
            }}
            size="small"
          />
        );
      default:
        return null;
    }
  };

  const saveButton = (row: ISetting) => (
    <Button
      type="link"
      size="small"
      loading={loading}
      title="Save"
      icon={<SaveFilled />}
      onClick={() => {
        setLoading(true);
        updateSetting(row)
          .then(() => {
            message.success(`${row.key} value updated successfully`);
          })
          .catch((e) => {
            message.error(e.message);
          })
          .finally(() => {
            setLoading(false);
          });
      }}
    >
      Save
    </Button>
  );

  if (isCompact) {
    return (
      <div>
        {settings.map((row) => (
          <div
            key={row.key}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: 12,
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8, wordBreak: "break-word" }}>
              {row.title || row.key}
            </div>
            {renderValue(row)}
            <div style={{ marginTop: 4 }}>{saveButton(row)}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <Table
        size="small"
        pagination={false}
        dataSource={settings}
        columns={[
          {
            title: "Setting",
            dataIndex: "title",
            key: "title",
            width: 200,
            render: (title: string, row: ISetting) => title || row.key,
          },
          {
            title: "Value",
            dataIndex: "key",
            key: "value",
            render: (_value: string, row: ISetting) => renderValue(row),
          },
          {
            title: "#",
            key: "actions",
            width: 100,
            render: (_value: string, row: ISetting) => saveButton(row),
          },
        ]}
      />{" "}
    </>
  );
});

AppSettingsForm.displayName = "AppSettingsForm";

export default AppSettingsForm;
